'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Vitamins and Supplements: Capsules, Gummies & Powders"
      author="MicroPlastics Editorial Team"
      date="June 5, 2025"
      readTime="10 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in vitamins and supplements, capsules, gummies, powders"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        A 2024 peer-reviewed study published in <em>PMC / Marine Pollution
        Bulletin</em> tested omega-3 supplements and found microplastics in
        100% of samples, <strong>polypropylene was the dominant polymer at
        50%+</strong>, with PET accounting for ~36% in some capsule oils.
        Beyond the oil, the <strong>capsule shell itself</strong> can be a
        plastic source: HPMC (hydroxypropyl methylcellulose) is the most
        common synthetic capsule polymer. Gelatin capsules and softgels are
        biological (cow/pork/fish), but the supplement industry generally
        relies on plastic packaging that contributes additional exposure.
        Safer: <strong>glass-bottled supplements, gelatin or pullulan capsules,
        single-ingredient powders in glass jars</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The 2024 omega-3 supplement study found microplastics in 100% of tested samples. PP dominant (50%+) and PET in capsule oils (~36%).</>,
          <>Capsule shells: <strong>HPMC is synthetic plastic polymer</strong>; gelatin and pullulan are biological alternatives.</>,
          <>Plastic supplement bottles add ongoing migration during shelf life (1-3 years for most vitamins).</>,
          <>Gummies contain gelatin or pectin + glycerin + colours; not microplastic-rich but often plastic-bagged.</>,
          <>Cleanest: powdered supplements in glass jars, gelatin capsules in glass bottles, food-form nutrition through diet.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2024 omega-3 study
      </h2>
      <p>
        A study published in <em>PMC / Marine Pollution Bulletin</em> in 2024
        analysed multiple omega-3 supplements (both plant-derived and
        fish-derived) for microplastic content. Key findings:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>100% of tested samples contained microplastic particles.</strong></li>
        <li>Polypropylene (PP, #5) was the dominant polymer at over 50% of total microplastics.</li>
        <li>PET accounted for over 36% in capsule oil samples, likely from packaging migration.</li>
        <li>Fish-derived omega-3s had similar contamination patterns to plant-derived, suggesting industrial processing equipment (not raw material source) is the main contamination route.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Capsule shell materials compared
      </h2>

      <BlogComparisonTable
        caption="Supplement capsule materials by microplastic safety"
        columns={[
          { key: 'material', label: 'Capsule material' },
          { key: 'source', label: 'Source' },
          { key: 'safety', label: 'Microplastic safety' },
          { key: 'notes', label: 'Notes' },
        ]}
        rows={[
          { material: 'Gelatin', source: 'Animal (cow / pork / fish)', safety: 'Plastic-free', notes: 'Traditional; not vegan; soft and quick-dissolving' },
          { material: 'Pullulan', source: 'Fermented tapioca starch', safety: 'Plastic-free', notes: 'Vegan; less common; expensive' },
          { material: 'Vegetable Cellulose (rice or pea-derived)', source: 'Plant-based', safety: 'Plastic-free (if uncoated)', notes: 'Verify no HPMC coating; some "vegetable cellulose" is HPMC' },
          { material: 'HPMC (Hydroxypropyl Methylcellulose) "veggie caps"', source: 'Chemically modified cellulose', safety: 'Synthetic polymer', notes: 'Plant-derived but chemically modified into plastic; most common "vegan" capsule' },
          { material: 'Softgel (gelatin + glycerin + water)', source: 'Animal gelatin', safety: 'Plastic-free shell', notes: 'Contents may have plastic from processing or packaging' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The packaging problem
      </h2>
      <p>
        Most supplements come in{' '}
        <Link href="/materials/hdpe" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          HDPE (#2)
        </Link>{' '}
        plastic bottles. The pills sit in
        direct contact with the plastic for the entire shelf life (1-3 years
        for most vitamins). Even chemically stable HDPE sheds particles over
        long contact periods, especially if the bottle has been exposed to
        heat (warehouse storage, mailbox delivery, summer car trips).
      </p>
      <p>
        Glass-bottled supplements are dramatically cleaner but harder to find.
        Brands worth knowing:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Pure Encapsulations</strong>, some products in glass.</li>
        <li><strong>New Chapter Organic</strong>, some products in glass.</li>
        <li><strong>Megafood</strong>, some products in glass.</li>
        <li><strong>Solgar</strong>, historically glass-bottled (verify per product).</li>
        <li><strong>Ancient Nutrition</strong>, multi-ingredient powders in glass-style jars.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Supplement formats compared
      </h2>

      <BlogComparisonTable
        caption="Supplement formats by microplastic exposure"
        columns={[
          { key: 'format', label: 'Format' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'notes', label: 'Notes' },
        ]}
        rows={[
          { format: 'Whole-food nutrition (diet)', risk: 'Lowest', notes: 'No supplements needed if diet is adequate' },
          { format: 'Bulk powder in glass jar', risk: 'Low', notes: 'Single-ingredient, no capsule, no plastic' },
          { format: 'Gelatin or pullulan capsules in glass bottle', risk: 'Low', notes: 'Plastic-free shell + plastic-free container' },
          { format: 'Gummies in glass jar', risk: 'Low-moderate', notes: 'Often packaging-only concern; gummies themselves are gelatin/pectin' },
          { format: 'Capsules in HDPE plastic bottle', risk: 'Moderate', notes: 'Most common format; bottle adds ongoing contamination' },
          { format: 'Softgels in HDPE bottle', risk: 'Moderate', notes: 'Especially fish oil; rancidity also accelerates plastic interaction' },
          { format: 'HPMC "veggie caps" in plastic bottle', risk: 'Higher', notes: 'Capsule shell + bottle both contribute' },
          { format: 'Individual blister-pack capsules', risk: 'Higher', notes: 'PVC + aluminium foil per dose' },
          { format: 'Liquid supplements in plastic bottles', risk: 'Higher', notes: 'Liquid contact with plastic for full shelf life' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Audit your supplement stack.</strong> Most adults need fewer supplements than they think, emphasise food sources first.</li>
        <li><strong>Choose glass-bottled products</strong> when available. Pure Encapsulations, MegaFood, New Chapter, Solgar.</li>
        <li><strong>Switch from HPMC &ldquo;veggie caps&rdquo; to gelatin or pullulan</strong> for the supplements you do take.</li>
        <li><strong>Transfer to glass at home.</strong> Decant supplements from plastic bottles into amber glass jars (e.g., 4 oz or 8 oz Boston rounds) when they arrive.</li>
        <li><strong>Buy in larger quantities</strong> from bulk suppliers to reduce packaging-per-dose.</li>
        <li><strong>Skip gummies</strong> when you can, they often have plastic packaging plus sugar and synthetic dyes; capsules in glass are cleaner.</li>
        <li><strong>For omega-3 specifically</strong>, eat actual fish (sardines, mackerel, salmon) 2-3x weekly instead of supplements when possible. Fish has been studied for microplastic content but the body of evidence is more mature.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-prenatal-vitamins" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in prenatal vitamins
        </Link>,{' '}
        <Link href="/blog/microplastics-in-seafood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in seafood
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-protein-powder-supplements" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in protein powder and supplements
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do vitamins contain microplastics?',
            answer:
              'Yes. A 2024 study published in Marine Pollution Bulletin found microplastics in 100% of tested omega-3 supplements, with polypropylene (PP) as the dominant polymer (50%+) and PET in capsule oils (~36%). Industrial processing equipment and plastic packaging are the main sources.',
          },
          {
            question: 'Are HPMC capsules plastic?',
            answer:
              'HPMC (hydroxypropyl methylcellulose) is a chemically modified cellulose polymer, plant-derived but converted into a synthetic plastic. It is the most common "vegan" capsule material. Gelatin (animal-derived) and pullulan (fermented tapioca starch) are non-plastic alternatives.',
          },
          {
            question: 'Are gelatin capsules safer than veggie capsules?',
            answer:
              'Yes for microplastic exposure. Gelatin is animal-derived protein with no plastic. Most "veggie capsules" are made of HPMC, which is a synthetic plastic polymer. Pullulan is a fully vegan plastic-free alternative but less common.',
          },
          {
            question: 'What is the safest supplement brand?',
            answer:
              'Glass-bottled supplements from Pure Encapsulations, MegaFood, New Chapter, or Solgar are evidence-supported choices. Single-ingredient powders in glass jars (bulk vitamin C, magnesium, collagen) eliminate capsule concerns entirely.',
          },
          {
            question: 'Are gummy vitamins bad?',
            answer:
              'Gummies themselves are typically gelatin or pectin-based, not microplastic-rich. The bigger issue is plastic packaging, added sugar, synthetic dyes, and lower nutrient density than capsules. Glass-jarred gummies from MegaFood and Ritual are cleaner options.',
          },
          {
            question: 'Should I get vitamins from food instead?',
            answer:
              'Yes when possible. A varied diet with vegetables, fruits, lean protein, whole grains, fish, and nuts covers most nutrient needs. Supplements are useful for documented deficiencies (B12 for vegans, D3 in winter, iron with diagnosis) but should not replace food-based nutrition.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Liu Q, Wang Y, Xue Y, et al.',
            year: 2024,
            title: 'Determination of Microplastics in Omega-3 Oil Supplements',
            publisher: 'Marine Pollution Bulletin / PMC',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11120427/',
          },
          {
            authors: 'Al-Tabakha MM',
            year: 2010,
            title: 'HPMC capsules: current status and future prospects',
            publisher: 'Journal of Pharmaceutical Sciences',
            url: 'https://pubmed.ncbi.nlm.nih.gov/21092714/',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2024,
            title: 'Food supplements: scientific guidance',
            publisher: 'EFSA',
            url: 'https://www.efsa.europa.eu/en/topics/topic/food-supplements',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Dietary Supplements regulations',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/dietary-supplements',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
